import React, { useState } from 'react';
import { X, Send, CheckCircle, Clock, Lock, MessageSquare } from 'lucide-react';
import { helpService } from '../helpService';
import { SupportTicket } from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  ticket: SupportTicket;
  isSystemAdmin: boolean;
  userDisplayName: string;
  onUpdated: (ticket: SupportTicket) => void;
}

export const TicketDetailModal: React.FC<Props> = ({
  isOpen,
  onClose,
  ticket,
  isSystemAdmin,
  userDisplayName,
  onUpdated,
}) => {
  const [messages, setMessages] = useState(ticket.messages || []);
  const [replyText, setReplyText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(ticket.status);
  const [resolutionNotes, setResolutionNotes] = useState(ticket.resolution_notes || '');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  if (!isOpen) return null;

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    try {
      setSending(true);
      const newMsg = await helpService.addTicketMessage(ticket.id, replyText.trim(), isInternalNote);
      setMessages((prev) => [...prev, newMsg]);
      setReplyText('');
      setIsInternalNote(false);

      // Refresh ticket details
      const refreshed = await helpService.getTicketById(ticket.id);
      setCurrentStatus(refreshed.status);
      onUpdated(refreshed);
    } catch (err: any) {
      alert('Failed to send message: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      setUpdatingStatus(true);
      const updated = await helpService.updateTicketStatus(ticket.id, newStatus, resolutionNotes);
      setCurrentStatus(updated.status);
      onUpdated(updated);
    } catch (err: any) {
      alert('Failed to update status: ' + err.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'OPEN':
        return <span className="badge-pill badge-warning">Open / Under Review</span>;
      case 'IN_PROGRESS':
        return <span className="badge-pill badge-info">In Progress / Responded</span>;
      case 'WAITING_ON_USER':
        return <span className="badge-pill badge-neutral">Waiting on User</span>;
      case 'RESOLVED':
        return <span className="badge-pill badge-success">Resolved</span>;
      case 'CLOSED':
        return <span className="badge-pill badge-neutral">Closed</span>;
      default:
        return <span className="badge-pill badge-neutral">{status}</span>;
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ display: 'flex', flexDirection: 'column', height: '85vh' }}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>
                {ticket.ticket_number}
              </span>
              <span>•</span>
              {getStatusBadge(currentStatus)}
              <span>•</span>
              <span className="badge-pill badge-neutral">{ticket.category}</span>
              {ticket.organization_name && (
                <>
                  <span>•</span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    🏢 {ticket.organization_name}
                  </span>
                </>
              )}
            </div>
            <h3>{ticket.subject}</h3>
            <p>Requester: <strong>{ticket.requester_name}</strong> • Created: {new Date(ticket.created_at).toLocaleString()}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Admin Control Bar */}
        {isSystemAdmin && (
          <div
            style={{
              padding: '12px 24px',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                Support Queue Status:
              </span>
              <select
                value={currentStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={updatingStatus}
                style={{ fontSize: '13px', padding: '6px 12px' }}
              >
                <option value="OPEN">Open (OPEN)</option>
                <option value="IN_PROGRESS">In Progress (IN_PROGRESS)</option>
                <option value="WAITING_ON_USER">Waiting on User</option>
                <option value="RESOLVED">Resolved (RESOLVED)</option>
                <option value="CLOSED">Closed (CLOSED)</option>
              </select>
            </div>

            {currentStatus === 'RESOLVED' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="Resolution notes / comments..."
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  style={{ fontSize: '12px', padding: '6px 10px', width: '280px' }}
                />
                <button
                  onClick={() => handleStatusChange('RESOLVED')}
                  className="btn-primary"
                  style={{ width: 'auto', padding: '6px 12px', fontSize: '12px' }}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        )}

        {/* Message Thread Body */}
        <div
          className="modal-body"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            background: '#f1f5f9',
          }}
        >
          {messages.map((m) => {
            const isAdmin = m.sender_role === 'SYSTEM_ADMIN';
            return (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isAdmin ? 'flex-start' : 'flex-end',
                }}
              >
                <div
                  style={{
                    maxWidth: '75%',
                    background: m.is_internal_note
                      ? '#fef3c7'
                      : isAdmin
                      ? '#ffffff'
                      : '#eff6ff',
                    border: m.is_internal_note
                      ? '1px solid #fde68a'
                      : isAdmin
                      ? '1px solid #e2e8f0'
                      : '1px solid #bfdbfe',
                    borderRadius: '12px',
                    padding: '14px 18px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      marginBottom: '6px',
                      fontSize: '11px',
                    }}
                  >
                    <span style={{ fontWeight: 700, color: isAdmin ? '#1e40af' : '#0369a1' }}>
                      {isAdmin ? '🛡️ ' : '👤 '}
                      {m.sender_name}
                      {isAdmin && ' (Support Team)'}
                      {m.is_internal_note && ' • 🔒 Internal Note'}
                    </span>
                    <span style={{ color: '#94a3b8' }}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#1e293b', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {m.message}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply Box Footer */}
        <div style={{ padding: '16px 24px', background: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
          <form onSubmit={handleSendMessage}>
            {isSystemAdmin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  id="internalCheck"
                  checked={isInternalNote}
                  onChange={(e) => setIsInternalNote(e.target.checked)}
                />
                <label htmlFor="internalCheck" style={{ fontSize: '12px', color: '#b45309', cursor: 'pointer', fontWeight: 600 }}>
                  <Lock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                  Add as internal note (visible only to support team)
                </label>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
              <textarea
                rows={2}
                placeholder={
                  isInternalNote
                    ? 'Write an internal note visible only to support team...'
                    : 'Type your reply here...'
                }
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  resize: 'none',
                }}
              />
              <button
                type="submit"
                disabled={sending || !replyText.trim()}
                className="btn-primary"
                style={{ width: 'auto', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Send size={16} />
                <span>{sending ? 'Sending...' : 'Send'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
